/**
 * FolderTile for the Documents page folder grid.
 *
 * The Documents page's primary view is a grid of folder tiles. Each tile
 * is a self-contained card with a coloured icon block, primary name,
 * secondary metadata (count / "Empty"), and a kebab menu for folder-level
 * actions. The admin dialogs those actions open live in FolderDialogs.tsx.
 */

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Folder,
  FolderPlus,
  MoreVertical,
  Pencil,
  Trash2,
  FileText,
  ChevronRight,
  Info,
  Users,
} from 'lucide-react';
import { CategoryWithDocuments } from '../../lib/api';
import { getCategoryColor } from './utils';

interface FolderTileProps {
  category: CategoryWithDocuments;
  onOpen: () => void;
  /** CR-7754: open the folder's Details / History window. Only passed on
   *  the customer side — suppliers never get it. */
  onDetails?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  /** Create a subfolder nested under this folder. Only passed when the
   *  current user is permitted to nest folders (any customer-side user,
   *  staff or super admin) — see CanCreateSubfolder on the backend. */
  onCreateSubfolder?: () => void;
  /** Assign which suppliers may see this folder. Only passed for customer
   *  admins — the backend applies the same gate. */
  onManageAccess?: () => void;
  /** 'card' = self-contained tile (default). 'row' = compact row for the
   *  list-layout toggle, designed to sit inside a divide-y container. */
  variant?: 'card' | 'row';
}

/**
 * Polished top-level folder tile used by FolderView. Renders the folder
 * icon in a coloured block, the name + description, the document count
 * (or "Empty" hint), and a hover-revealed kebab menu when admin handlers
 * are provided. Supports a compact row variant for the list-layout toggle.
 */
export function FolderTile({ category, onOpen, onDetails, onRename, onDelete, onCreateSubfolder, onManageAccess, variant = 'card' }: FolderTileProps) {
  const count = category.document_count;
  // Container folders hold no files of their own but nest populated
  // subfolders. Show their rolled-up shape ("3 folders · 331 documents")
  // instead of a misleading "Empty" — only a folder with no direct docs
  // AND no subfolders is truly empty.
  const subfolderCount = category.subfolder_count ?? 0;
  const totalDocs = category.total_document_count ?? count;
  const isEmpty = count === 0 && subfolderCount === 0;
  const isContainer = count === 0 && subfolderCount > 0;
  // Re-use the category-color palette as a solid soft icon block. The
  // CATEGORY_COLOR_MAP already covers dark mode.
  const colorClasses = getCategoryColor(category.color);
  const hasActions = Boolean(onDetails || onRename || onDelete || onCreateSubfolder || onManageAccess);

  // Secondary metadata line — rendered identically in both variants.
  const metaContent = isEmpty ? (
    <span className="italic">Empty</span>
  ) : isContainer ? (
    <span className="inline-flex items-center gap-1.5">
      <Folder className="h-3.5 w-3.5" />
      <span className="tabular-nums">{subfolderCount}</span>
      <span>{subfolderCount === 1 ? 'folder' : 'folders'}</span>
      {totalDocs > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">{totalDocs}</span>
          <span>{totalDocs === 1 ? 'document' : 'documents'}</span>
        </>
      )}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <FileText className="h-3.5 w-3.5" />
      <span className="tabular-nums">{count}</span>
      <span>{count === 1 ? 'document' : 'documents'}</span>
    </span>
  );

  // Shared menu body — rendered identically in the row and card variants.
  const menuItems = (
    <>
      {onDetails && (
        <DropdownMenuItem onClick={onDetails}>
          <Info className="h-4 w-4 mr-2" />Details
        </DropdownMenuItem>
      )}
      {onCreateSubfolder && (
        <DropdownMenuItem onClick={onCreateSubfolder}>
          <FolderPlus className="h-4 w-4 mr-2" />New subfolder
        </DropdownMenuItem>
      )}
      {onRename && (
        <DropdownMenuItem onClick={onRename}>
          <Pencil className="h-4 w-4 mr-2" />Rename
        </DropdownMenuItem>
      )}
      {onManageAccess && (
        <DropdownMenuItem onClick={onManageAccess}>
          <Users className="h-4 w-4 mr-2" />Manage access
        </DropdownMenuItem>
      )}
      {(onDetails || onCreateSubfolder || onRename || onManageAccess) && onDelete && <DropdownMenuSeparator />}
      {onDelete && (
        <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />Delete folder
        </DropdownMenuItem>
      )}
    </>
  );

  const actionsMenu = hasActions ? (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${category.name} actions`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {menuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // Compact row layout — single horizontal line, no Card wrapper. Used by
  // the List layout toggle. Sits inside a divide-y border container.
  if (variant === 'row') {
    return (
      <div
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
        className="group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/50"
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${colorClasses}`}
          aria-hidden="true"
        >
          <Folder className="h-4.5 w-4.5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate" title={category.name}>
            {category.name}
          </div>
          {category.description && (
            <div className="text-xs text-muted-foreground truncate">
              {category.description}
            </div>
          )}
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          {metaContent}
        </div>
        {hasActions && <div className="-mr-2">{actionsMenu}</div>}
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }

  return (
    <Card
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative cursor-pointer transition-all duration-150 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${colorClasses}`}
            aria-hidden="true"
          >
            <Folder className="h-6 w-6" strokeWidth={1.75} />
          </div>
          {hasActions && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 -mr-1 -mt-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${category.name} actions`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {menuItems}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="mt-3">
          <h3
            className="font-semibold text-base leading-tight line-clamp-2 break-words"
            title={category.name}
          >
            {category.name}
          </h3>
          {category.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {category.description}
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          {metaContent}
          <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5 font-medium">
            Open
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
